import { createBigDecimal } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { Chain } from '../../chain'
import { createPolkadotAddress, createPolkadotAssetId } from '../../utils'
import {
  assetsBurnedSchema,
  assetsIssuedSchema,
  assetsTransferredSchema,
  balancesBurnedSchema,
  balancesDepositSchema,
  balancesMintedSchema,
  balancesTransferSchema,
  balancesWithdrawSchema,
  tokensDepositedSchema,
  tokensTransferSchema,
  tokensWithdrawnSchema,
} from './schemas'

export const createAssetsBurnedTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = assetsBurnedSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { assetId, owner, balance } = parsed.data
  if (balance <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(owner)) {
    return undefined
  }

  const assetWithDecimals = chain.assetMap.get(assetId)
  if (assetWithDecimals === undefined) {
    return {
      direction: 'out',
      asset: {
        symbol: assetId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId,
        }),
        name: assetId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(balance, 0),
      from: createPolkadotAddress({
        chainId: chain.id,
        address: owner,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction: 'out',
    asset,
    amount: createBigDecimal(balance, decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: owner,
    }),
  }
}

export const createAssetsIssuedTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = assetsIssuedSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { assetId, owner, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(owner)) {
    return undefined
  }

  const assetWithDecimals = chain.assetMap.get(assetId)
  if (assetWithDecimals === undefined) {
    return {
      direction: 'in',
      asset: {
        symbol: assetId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId,
        }),
        name: assetId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(amount, 0),
      to: createPolkadotAddress({
        chainId: chain.id,
        address: owner,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction: 'in',
    asset,
    amount: createBigDecimal(amount, decimals),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: owner,
    }),
  }
}

export const createAssetsTransferredTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = assetsTransferredSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { assetId, from, to, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(from) && !relatedAddresses.has(to)) {
    return undefined
  }
  let direction: Transfer['direction']
  if (relatedAddresses.has(from) && relatedAddresses.has(to)) {
    direction = 'none'
  } else if (relatedAddresses.has(from)) {
    direction = 'out'
  } else {
    direction = 'in'
  }

  const assetWithDecimals = chain.assetMap.get(assetId)
  if (assetWithDecimals === undefined) {
    return {
      direction,
      asset: {
        symbol: assetId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId,
        }),
        name: assetId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(amount, 0),
      from: createPolkadotAddress({
        chainId: chain.id,
        address: from,
      }),
      to: createPolkadotAddress({
        chainId: chain.id,
        address: to,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction,
    asset,
    amount: createBigDecimal(amount, decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: from,
    }),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: to,
    }),
  }
}

export const createBalancesBurnedTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = balancesBurnedSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  return {
    direction: 'out',
    asset: chain.nativeToken.currency,
    amount: createBigDecimal(amount, chain.nativeToken.decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}

export const createBalancesDepositTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = balancesDepositSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  return {
    direction: 'in',
    asset: chain.nativeToken.currency,
    amount: createBigDecimal(amount, chain.nativeToken.decimals),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}

export const createBalancesMintedTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = balancesMintedSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  return {
    direction: 'in',
    asset: chain.nativeToken.currency,
    amount: createBigDecimal(amount, chain.nativeToken.decimals),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}

export const createBalancesTransferTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = balancesTransferSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { from, to, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(from) && !relatedAddresses.has(to)) {
    return undefined
  }
  let direction: Transfer['direction']
  if (relatedAddresses.has(from) && relatedAddresses.has(to)) {
    direction = 'none'
  } else if (relatedAddresses.has(from)) {
    direction = 'out'
  } else {
    direction = 'in'
  }

  return {
    direction,
    asset: chain.nativeToken.currency,
    amount: createBigDecimal(amount, chain.nativeToken.decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: from,
    }),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: to,
    }),
  }
}

export const createBalancesWithdrawTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = balancesWithdrawSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  return {
    direction: 'out',
    asset: chain.nativeToken.currency,
    amount: createBigDecimal(amount, chain.nativeToken.decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}

export const createTokensDepositedTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = tokensDepositedSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { currencyId, who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  const assetWithDecimals = chain.assetMap.get(currencyId)
  if (assetWithDecimals === undefined) {
    return {
      direction: 'in',
      asset: {
        symbol: currencyId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId: currencyId,
        }),
        name: currencyId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(amount, 0),
      to: createPolkadotAddress({
        chainId: chain.id,
        address: who,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction: 'in',
    asset,
    amount: createBigDecimal(amount, decimals),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}

export const createTokensTransferTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = tokensTransferSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { currencyId, from, to, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(from) && !relatedAddresses.has(to)) {
    return undefined
  }
  let direction: Transfer['direction']
  if (relatedAddresses.has(from) && relatedAddresses.has(to)) {
    direction = 'none'
  } else if (relatedAddresses.has(from)) {
    direction = 'out'
  } else {
    direction = 'in'
  }

  const assetWithDecimals = chain.assetMap.get(currencyId)
  if (assetWithDecimals === undefined) {
    return {
      direction,
      asset: {
        symbol: currencyId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId: currencyId,
        }),
        name: currencyId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(amount, 0),
      from: createPolkadotAddress({
        chainId: chain.id,
        address: from,
      }),
      to: createPolkadotAddress({
        chainId: chain.id,
        address: to,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction,
    asset,
    amount: createBigDecimal(amount, decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: from,
    }),
    to: createPolkadotAddress({
      chainId: chain.id,
      address: to,
    }),
  }
}

export const createTokensWithdrawnTransfer = (params: {
  chain: Chain
  data: Record<string, unknown> | unknown[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}): Transfer | undefined => {
  const { chain, data, relatedAddresses, debug } = params
  const parsed = tokensWithdrawnSchema.safeParse(data)
  if (!parsed.success) {
    if (debug !== undefined) {
      debug(parsed.error)
    }
    return undefined
  }
  const { currencyId, who, amount } = parsed.data
  if (amount <= 0n) {
    return undefined
  }
  if (!relatedAddresses.has(who)) {
    return undefined
  }

  const assetWithDecimals = chain.assetMap.get(currencyId)
  if (assetWithDecimals === undefined) {
    return {
      direction: 'out',
      asset: {
        symbol: currencyId.toString(),
        type: 'CryptoCurrency',
        id: createPolkadotAssetId({
          chainId: chain.id,
          assetId: currencyId,
        }),
        name: currencyId.toString(),
        updatedAt: 0,
      },
      amount: createBigDecimal(amount, 0),
      from: createPolkadotAddress({
        chainId: chain.id,
        address: who,
      }),
    }
  }

  const { asset, decimals } = assetWithDecimals
  return {
    direction: 'out',
    asset,
    amount: createBigDecimal(amount, decimals),
    from: createPolkadotAddress({
      chainId: chain.id,
      address: who,
    }),
  }
}
